import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
} from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';
import { db } from './config';

/**
 * Cart item data structure in Firestore:
 * {
 *   userId: string,
 *   name: string,
 *   image: string,           // product thumbnail URL
 *   type: string,
 *   color: string,
 *   designShape: string,
 *   printLocation: string,
 *   quantities: Object,      // { xs: 0, small: 2, medium: 3, ... }
 *   quantity: number,        // total across all sizes
 *   price: number,
 *   note: string,
 *   sizeBreakdown: string,
 *   designImages: {          // Firebase Storage URLs keyed by imprint location name
 *     'Full Front': string|null,
 *     'Left Chest': string|null,
 *     // ...any location name from IMPRINT_PRICES tables
 *   },
 *   createdAt: Timestamp
 * }
 */

/**
 * Get all cart items for a specific user.
 */
export async function getCartItems(userId) {
    if (!userId) return { items: [], error: 'No user ID provided.' };

    try {
        const cartRef = collection(db, 'carts');
        const q = query(cartRef, where('userId', '==', userId));
        const snapshot = await getDocs(q);

        const items = [];
        snapshot.forEach((docSnap) => {
            items.push({ id: docSnap.id, ...docSnap.data() });
        });

        items.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        return { items, error: null };
    } catch (error) {
        console.error('Error fetching cart items:', error);
        return { items: [], error: error.message };
    }
}

/**
 * Add a new item to the user's cart in Firestore.
 * Now includes designImages (Firebase Storage URLs).
 */
export async function addCartItem(userId, cartItem) {
    if (!userId) return { itemId: null, error: 'No user ID provided.' };
    if (!cartItem) return { itemId: null, error: 'No cart item data provided.' };

    try {
        const cartRef = collection(db, 'carts');

        const cartData = {
            userId,
            name:          cartItem.name          || '',
            image:         cartItem.image          || '',
            imageUrl:      cartItem.imageUrl       || '',
            type:          cartItem.type           || '',
            color:         cartItem.color          || '',
            subcategory:   cartItem.subcategory    || '',   
            category:      cartItem.category       || '',
            selectedAttributes: cartItem.selectedAttributes && typeof cartItem.selectedAttributes === 'object'
                ? cartItem.selectedAttributes
                : {},
            designShape:   cartItem.designShape    || '',
            printLocation: cartItem.printLocation  || '',
            quantities:    cartItem.quantities     || {},
            quantity:      cartItem.quantity       || 0,
            price:         cartItem.price          || 0,
            sizePrices:    cartItem.sizePrices && typeof cartItem.sizePrices === 'object'
                ? cartItem.sizePrices
                : {},
            teamName:      cartItem.teamName       || '',
            surname:       cartItem.surname        || '',
            number:        cartItem.number         || '',
            note:          cartItem.note           || '',
            sizeBreakdown: cartItem.sizeBreakdown  || '',
            designImages:  cartItem.designImages && typeof cartItem.designImages === 'object'
            ? cartItem.designImages
            : {},
            designEmailSent: Boolean(cartItem.designEmailSent),
            designEmailSentAt: cartItem.designEmailSentAt || null,
            storeEmail: cartItem.storeEmail || '',
            colorSurcharge: Number(cartItem.colorSurcharge || 0),
            createdAt: serverTimestamp(),
        };

        const docRef = await addDoc(cartRef, cartData);
        return { itemId: docRef.id, error: null };
    } catch (error) {
        console.error('Error adding cart item:', error);
        let errorMessage = error.message;
        if (error.code === 'permission-denied') errorMessage = 'Permission denied. Please check your Firestore security rules.';
        else if (error.code === 'unavailable')  errorMessage = 'Firebase service is currently unavailable. Please try again later.';
        else if (error.code === 'unauthenticated') errorMessage = 'You must be logged in to add items to cart.';
        return { itemId: null, error: errorMessage };
    }
}

/**
 * Update specific fields of a cart item in Firestore.
 * Used to persist size/quantity edits made in the Cart page.
 *
 * @param {string} itemId  - Firestore document ID of the cart item
 * @param {Object} updates - Fields to update e.g. { quantities, quantity, sizeBreakdown }
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function updateCartItem(itemId, updates) {
    if (!itemId) return { success: false, error: 'No item ID provided.' };

    try {
        const cartDocRef = doc(db, 'carts', itemId);
        await updateDoc(cartDocRef, updates);
        return { success: true, error: null };
    } catch (error) {
        console.error('Error updating cart item:', error);
        let errorMessage = error.message;
        if (error.code === 'permission-denied') errorMessage = 'Permission denied. Please check your Firestore security rules.';
        else if (error.code === 'unavailable')  errorMessage = 'Firebase service is currently unavailable. Please try again later.';
        else if (error.code === 'unauthenticated') errorMessage = 'You must be logged in to update cart items.';
        return { success: false, error: errorMessage };
    }
}

/**
 * Remove a specific cart item from Firestore.
 */
export async function removeCartItem(itemId) {
    if (!itemId) return { success: false, error: 'No item ID provided.' };

    try {
        await deleteDoc(doc(db, 'carts', itemId));
        return { success: true, error: null };
    } catch (error) {
        console.error('Error removing cart item:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Clear all cart items for a specific user (e.g. after checkout).
 */
export async function clearCart(userId) {
    if (!userId) return { success: false, error: 'No user ID provided.' };

    try {
        const cartRef = collection(db, 'carts');
        const q = query(cartRef, where('userId', '==', userId));
        const snapshot = await getDocs(q);

        const deletePromises = [];
        snapshot.forEach((docSnap) => {
            deletePromises.push(deleteDoc(doc(db, 'carts', docSnap.id)));
        });

        await Promise.all(deletePromises);
        return { success: true, error: null };
    } catch (error) {
        console.error('Error clearing cart:', error);
        return { success: false, error: error.message };
    }
}